import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_IMAGES } from "@/data/images";
import { MapPreview } from "@/components/MapPreview";
import { Users, Clock, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";

const times = [];
for (let h = 0; h < 24; h++) {
  for (const m of [0, 30]) {
    times.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

export function Step1ServiceSelect({ config, form, set, t }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {config.services.map((s) => (
        <button key={s.id} data-testid={`select-service-${s.id}`}
          onClick={() => set("service_type", s.id)}
          className={`text-left rounded-lg overflow-hidden border transition-all ${
            form.service_type === s.id ? "border-white ring-1 ring-white" : "border-white/10 hover:border-white/40"}`}>
          <div className="h-40 overflow-hidden">
            <img src={SERVICE_IMAGES[s.id]} alt={s.label} className="w-full h-full object-cover" />
          </div>
          <div className="p-5 bg-card">
            <h3 className="text-white font-medium">{t(`svc.${s.id}.title`)}</h3>
            <p className="text-white/50 text-xs mt-0.5">{s.vehicle}</p>
            <p className="text-muted-foreground text-xs mt-1">
              {s.type === "preset" ? t("booking.presetHint") : `${t("booking.startingFrom")} $${s.base} · $${s.per_hour}/hr`}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

const newStop = () => ({
  _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `stop_${Math.random().toString(36).slice(2)}`,
  location: "", place: null,
});

function TransitPackages({ config, form, set, t }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-luxe text-muted-foreground mb-3 block">{t("booking.choosePackage")}</Label>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="transit-packages">
        {(config.transit_packages || []).map((p) => (
          <button key={p.id} type="button" data-testid={`select-package-${p.id}`}
            onClick={() => set("package_id", p.id)}
            className={`text-left rounded-lg border p-4 transition-all ${
              form.package_id === p.id ? "border-white ring-1 ring-white bg-white/5" : "border-white/10 hover:border-white/40"}`}>
            <p className="text-white text-sm font-medium">{p.label}</p>
            <p className="text-muted-foreground text-xs mt-1">{p.hint}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function TripLocations({ isTransit, form, set, t }) {
  return (
    <div className={`grid gap-5 ${isTransit ? "sm:grid-cols-1" : "sm:grid-cols-2"}`}>
      <div>
        <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.pickup")}</Label>
        <div className="mt-2">
          <AddressAutocomplete
            testId="booking-pickup"
            value={form.pickup_location}
            placeholder={t("booking.addressPlaceholder")}
            onChange={(v) => set("pickup_location", v)}
            onSelect={(place) => {
              if (place) { set("pickup_location", place.formattedAddress); set("pickup_coords", place.coords); set("pickup_place", place); }
              else { set("pickup_coords", null); set("pickup_place", null); }
            }}
          />
        </div>
        {form.pickup_location && !form.pickup_coords && (
          <p className="text-xs text-destructive mt-1.5" data-testid="pickup-validation-msg">{t("booking.invalidPickup")}</p>
        )}
      </div>
      {!isTransit && (
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.dropoff")}</Label>
          <div className="mt-2">
            <AddressAutocomplete
              testId="booking-dropoff"
              value={form.dropoff_location}
              placeholder={t("booking.addressPlaceholder")}
              onChange={(v) => set("dropoff_location", v)}
              onSelect={(place) => {
                if (place) { set("dropoff_location", place.formattedAddress); set("dropoff_coords", place.coords); set("dropoff_place", place); }
                else { set("dropoff_coords", null); set("dropoff_place", null); }
              }}
            />
          </div>
          {form.dropoff_location && !form.dropoff_coords && (
            <p className="text-xs text-destructive mt-1.5" data-testid="dropoff-validation-msg">{t("booking.invalidDropoff")}</p>
          )}
        </div>
      )}
    </div>
  );
}

function ExtraStops({ form, set, t }) {
  const stops = form.extra_stops || [];
  return (
    <div data-testid="additional-stops">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.stopLabel")}s</Label>
        <button type="button" data-testid="add-stop-btn"
          onClick={() => set("extra_stops", [...stops, newStop()])}
          className="text-xs inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-white/80 hover:border-white/40 transition-colors">
          <Plus className="h-3.5 w-3.5" /> {t("booking.addStop")}
        </button>
      </div>
      <div className="space-y-3">
        {stops.map((stop, i) => (
          <div key={stop._key || i} className="flex items-start gap-2" data-testid={`stop-row-${i}`}>
            <div className="flex-1">
              <AddressAutocomplete
                testId={`booking-stop-${i}`}
                value={stop.location}
                placeholder={t("booking.stopPlaceholder")}
                onChange={(v) => {
                  const next = [...stops];
                  next[i] = { ...next[i], location: v };
                  set("extra_stops", next);
                }}
                onSelect={(place) => {
                  const next = [...stops];
                  next[i] = place
                    ? { ...next[i], location: place.formattedAddress, place }
                    : { ...next[i], place: null };
                  set("extra_stops", next);
                }}
              />
            </div>
            <button type="button" data-testid={`remove-stop-${i}`}
              onClick={() => set("extra_stops", stops.filter((_, j) => j !== i))}
              className="h-12 w-10 flex items-center justify-center rounded-lg border border-white/10 text-muted-foreground hover:text-destructive hover:border-white/30 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TripOptions({ form, set, t }) {
  return (
    <div className="space-y-5">
      <div>
        <Label className="text-xs uppercase tracking-luxe text-muted-foreground mb-2 block">{t("booking.tripType")}</Label>
        <div className="flex flex-wrap gap-2" data-testid="trip-type-group">
          {[["one_way", t("booking.tt.one_way")], ["round_trip", t("booking.tt.round_trip")], ["hourly", t("booking.tt.hourly")], ["multi_day", t("booking.tt.multi_day")]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`trip-type-${v}`}
              onClick={() => set("trip_type", v)}
              className={`rounded-full px-4 py-2 text-sm border transition-all ${form.trip_type === v ? "bg-white text-black border-white" : "bg-transparent text-white/80 border-white/15 hover:border-white/40"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.luggage")}</Label>
          <Input data-testid="booking-luggage" type="number" min="0" value={form.luggage}
            onChange={(e) => set("luggage", e.target.value)}
            className="mt-2 h-12 bg-secondary border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.flight")}</Label>
          <Input data-testid="booking-flight" value={form.flight_number}
            onChange={(e) => set("flight_number", e.target.value)}
            className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="AC 8642" />
        </div>
      </div>
    </div>
  );
}

function PassengerTimeDate({ isTransit, form, set, date, setDate, t }) {
  return (
    <>
      <div className="grid sm:grid-cols-3 gap-5">
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.passengers")}</Label>
          <div className="relative mt-2">
            <Users className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
            <Input data-testid="booking-passengers" type="number" min="1" value={form.passengers}
              onChange={(e) => set("passengers", e.target.value)}
              className="h-12 pl-10 bg-secondary border-white/10 text-white" />
          </div>
        </div>
        {!isTransit && (
          <div>
            <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.hours")}</Label>
            <div className="relative mt-2">
              <Clock className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
              <Input data-testid="booking-hours" type="number" min="1" value={form.hours}
                onChange={(e) => set("hours", e.target.value)}
                className="h-12 pl-10 bg-secondary border-white/10 text-white" />
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.time")}</Label>
          <Select value={form.time} onValueChange={(v) => set("time", v)}>
            <SelectTrigger data-testid="booking-time" className="mt-2 h-12 bg-secondary border-white/10 text-white">
              <SelectValue placeholder={t("booking.selectTime")} />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {times.map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-luxe text-muted-foreground mb-2 block">{t("booking.selectDate")}</Label>
        <div className="rounded-lg border border-white/10 bg-card p-4 inline-block" data-testid="booking-calendar">
          <Calendar mode="single" selected={date} onSelect={setDate}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            className="text-white" />
        </div>
      </div>
    </>
  );
}

function VehiclePicker({ form, set, decision, isQuoteMode, availVehicles, t }) {
  return (
    <div data-testid="vehicle-selection">
      {decision && (
        <div className={`mb-4 rounded-lg border p-4 ${isQuoteMode ? "border-white/15 bg-secondary/50" : "border-white/30 bg-white/10"}`} data-testid="booking-mode-badge">
          <p className="text-white text-sm font-medium">
            {isQuoteMode ? t("booking.quoteRequired") : t("booking.instantAvailable")}
          </p>
          {isQuoteMode && (
            <p className="text-xs text-muted-foreground mt-1">{decision.reasons?.join(" · ")}</p>
          )}
        </div>
      )}
      {!isQuoteMode && (
        <>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground mb-3 block">{t("booking.selectVehicle")}</Label>
          <div className="grid sm:grid-cols-2 gap-3">
            {availVehicles.map((v) => (
              <button key={v.id} type="button" data-testid={`select-vehicle-${v.id}`}
                disabled={!v.available}
                onClick={() => set("vehicle_id", v.id)}
                className={`text-left rounded-lg border p-4 transition-all disabled:opacity-40 ${form.vehicle_id === v.id ? "border-white ring-1 ring-white bg-white/5" : "border-white/10 hover:border-white/40"}`}>
                <div className="flex justify-between items-start">
                  <p className="text-white text-sm font-medium">{v.name}</p>
                  <span className={`text-[10px] uppercase tracking-luxe ${v.available ? "text-white/60" : "text-destructive"}`}>
                    {v.available ? t("booking.available") : t("booking.unavailable")}
                  </span>
                </div>
                <p className="text-muted-foreground text-xs mt-1">{v.vehicle_class} · {v.capacity} pax · {v.luggage_capacity} bags</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(v.amenities || []).slice(0, 3).map((a) => <span key={a} className="text-[10px] text-white/50 border border-white/10 rounded-full px-2 py-0.5">{a}</span>)}
                </div>
              </button>
            ))}
            {availVehicles.length === 0 && (
              <p className="text-muted-foreground text-sm">{t("booking.noVehicles")}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function Step2TripDetails({ isTransit, config, form, set, date, setDate, decision, isQuoteMode, availVehicles, t }) {
  return (
    <div className="space-y-6">
      {isTransit && <TransitPackages config={config} form={form} set={set} t={t} />}
      <TripLocations isTransit={isTransit} form={form} set={set} t={t} />
      {!isTransit && <ExtraStops form={form} set={set} t={t} />}
      {!isTransit && <TripOptions form={form} set={set} t={t} />}
      <PassengerTimeDate isTransit={isTransit} form={form} set={set} date={date} setDate={setDate} t={t} />
      {!isTransit && date && form.time && (
        <VehiclePicker form={form} set={set} decision={decision} isQuoteMode={isQuoteMode} availVehicles={availVehicles} t={t} />
      )}
    </div>
  );
}

export function Step3ContactInfo({ config, form, set, t }) {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.fullName")}</Label>
          <Input data-testid="booking-name" value={form.name} onChange={(e) => set("name", e.target.value)}
            className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="John Doe" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("field.phone")}</Label>
          <Input data-testid="booking-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
            className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="+1 (514) 555-0199" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("field.email")}</Label>
        <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
          className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="you@email.com" />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("field.occasion")}</Label>
          <Input data-testid="booking-occasion" value={form.occasion} onChange={(e) => set("occasion", e.target.value)}
            className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="Wedding, prom, night out..." />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.leadSource")}</Label>
          <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
            <SelectTrigger data-testid="booking-lead-source" className="mt-2 h-12 bg-secondary border-white/10 text-white">
              <SelectValue placeholder="Select a source" />
            </SelectTrigger>
            <SelectContent>
              {config.lead_sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.requests")}</Label>
        <Textarea data-testid="booking-requests" value={form.special_requests}
          onChange={(e) => set("special_requests", e.target.value)}
          className="mt-2 bg-secondary border-white/10 text-white min-h-24" placeholder="Champagne, decorations, music preferences..." />
      </div>
    </div>
  );
}

export function Step4Review({ isTransit, config, form, selectedService, availVehicles, date, estimate, routeGeo, route, t }) {
  const rows = isTransit
    ? [
        [t("field.service"), t("svc.transit_bus.title")],
        [t("field.package"), (config.transit_packages || []).find((p) => p.id === form.package_id)?.label],
        [t("field.vehicle"), estimate?.vehicle],
        [t("field.date"), date ? format(date, "PPP") : "-"],
        [t("field.time"), form.time],
        [t("field.pickup"), form.pickup_location],
        [t("field.passengers"), form.passengers],
        [t("field.name"), form.name],
        [t("field.email"), form.email],
        [t("field.phone"), form.phone],
        [t("field.occasion"), form.occasion || "-"],
      ]
    : [
        [t("field.service"), t(`svc.${form.service_type}.title`)],
        [t("field.tripType"), form.trip_type?.replace("_", " ")],
        [t("field.vehicle"), availVehicles.find((v) => v.id === form.vehicle_id)?.name || selectedService?.vehicle],
        [t("field.date"), date ? format(date, "PPP") : "-"],
        [t("field.time"), form.time],
        [t("field.pickup"), form.pickup_location],
        [t("field.dropoff"), form.dropoff_location],
        [t("field.passengers"), form.passengers],
        [t("field.luggage"), form.luggage],
        [t("field.flight"), form.flight_number || "-"],
        [t("field.duration"), `${form.hours} ${t("booking.hoursLabel")}`],
        [t("field.name"), form.name],
        [t("field.email"), form.email],
        [t("field.phone"), form.phone],
      ];
  return (
    <div className="rounded-lg border border-white/10 bg-card p-8 space-y-4">
      <h3 className="font-serif text-2xl text-white mb-4">{t("booking.review")}</h3>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-white/5 pb-3 text-sm">
          <span className="text-muted-foreground">{k}</span>
          <span className="text-white text-right">{v}</span>
        </div>
      ))}
      {form.pickup_coords && (
        <div>
          {!isTransit && route && (
            <div className="flex justify-between text-sm pt-1">
              <span className="text-muted-foreground">{t("booking.estimatedTrip")}</span>
              <span className="text-white">{route.km} km · ≈ {route.min} {t("booking.minDrive")}</span>
            </div>
          )}
          <MapPreview
            pickup={form.pickup_coords}
            dropoff={isTransit ? null : form.dropoff_coords}
            stops={(form.extra_stops || []).filter((s) => s.place).map((s) => ({ lat: s.place.latitude, lng: s.place.longitude }))}
            routeGeo={routeGeo}
          />
        </div>
      )}
    </div>
  );
}
